import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {
    DEFAULT_CURRENCY_CODE,
    InjectionToken,
    LOCALE_ID,
    type EnvironmentProviders,
    type Provider,
} from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withPreloading,
    type Routes,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { SeoEffects } from '../+state/seo/seo.effects';
import { HyundaiPreloadingStrategy, withPreloadingToken } from '../utilities/preload';
import { provideCloudinaryImageConfig } from './cloudinary.provider';
import { provideEnvironmentSpecificProviders } from './env-specific-providers/providers';

export const isE2e = new InjectionToken<boolean>('isE2e');

export interface CoreProviderConfig {
    routes: Routes;
    cloudinaryBaseUrl: string;
    showroomScriptUrl?: string;
    e2e: boolean;
}

// eslint-disable-next-line max-lines-per-function
export function provideCore(config: CoreProviderConfig): Array<Provider | EnvironmentProviders> {
    return [
        provideClientHydration(),
        provideCloudinaryImageConfig({ baseUrl: config.cloudinaryBaseUrl }),
        provideRouter(
            config.routes,
            withEnabledBlockingInitialNavigation(),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
            withPreloading(HyundaiPreloadingStrategy),
        ),
        provideStore({ router: routerReducer }),
        provideRouterStore(),
        provideAnimationsAsync(),
        provideEffects([SeoEffects]),
        provideEnvironmentSpecificProviders(),
        {
            provide: withPreloadingToken,
            useValue: !config.e2e,
        },
        { provide: isE2e, useValue: config.e2e },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'EUR',
        },
        {
            provide: LOCALE_ID,
            useFactory: () => {
                registerLocaleData(localeDe, 'de-DE', localeDeExtra);
                return 'de-DE';
            },
        },
    ];
}
